import {
    ColumnDef,
    flexRender,
    getCoreRowModel,
    OnChangeFn,
    PaginationState,
    type Row,
    type RowData,
    RowSelectionState,
    SortingState,
    useReactTable,
} from "@tanstack/react-table";

declare module "@tanstack/react-table" {
    // eslint-disable-next-line @typescript-eslint/no-unused-vars
    interface ColumnMeta<TData extends RowData, TValue> {
        // Applied to the rendered `<TableHead>` and `<TableCell>`. Columns
        // that need a pinned or bounded width (e.g. the select column's
        // `w-12`, the name column's `min-w-[...] max-w-[...]`) set it here
        // instead of a numeric react-table `size`. This table intentionally
        // never sets an inline pixel `width` style: with auto layout (see
        // the `<Table>` below, no `table-fixed`), any column that DOES carry
        // an inline width gets a proportional share of the table's leftover
        // width once it's stretched to fill the container — even columns
        // that are already "sized" — so a naive 48px checkbox column still
        // grows past 48px on wide viewports. Leaving every column but Name
        // unconstrained (like the document library's Type/Fields/Client/etc.
        // columns) means Name is the only one that absorbs that leftover
        // space, so fixed-width columns like the checkbox stay pinned
        // regardless of viewport width.
        cellClassName?: string;
    }
}

import { DataTablePagination } from "@/components/shared/DataTablePagination";
import { Button } from "@/components/ui/button";
import { Skeleton } from "@/components/ui/skeleton";
import {
    Table,
    TableBody,
    TableCell,
    TableHead,
    TableHeader,
    TableRow,
} from "@/components/ui/table";
import { cn } from "@/lib/utils";
import { ArrowDown, ArrowUp, ArrowUpDown } from "lucide-react";

export interface DataTableState {
    icon: React.ReactNode;
    title: string;
    body: string;
}

export interface DataTableProps<TData, TValue> {
    data: TData[];
    columns: ColumnDef<TData, TValue>[];
    /** Pluralised noun for the pagination summary, e.g. "skills". */
    entityLabel: string;
    emptyState: DataTableState;
    errorState: DataTableState;
    /** Shown instead of `emptyState` when a search or filter is narrowing. */
    filteredState?: DataTableState;
    clearFiltersLabel?: string;
    /**
     * Set only by tables that clamp a past-the-end page. Leaving it undefined
     * also opts out of the inconsistent-page check below, so tables without
     * page recovery keep their plain empty state.
     */
    isRecovering?: boolean;
    recoveringState?: DataTableState;
    rowCount?: number;
    isLoading?: boolean;
    isError?: boolean;
    hasActiveFilters?: boolean;
    onClearFilters?: () => void;
    onRetry?: () => void;
    pagination: PaginationState;
    onPaginationChange: OnChangeFn<PaginationState>;
    sorting?: SortingState;
    onSortingChange?: OnChangeFn<SortingState>;
    rowSelection?: RowSelectionState;
    onRowSelectionChange?: OnChangeFn<RowSelectionState>;
    getRowId?: (row: TData) => string;
    /**
     * Checkbox tables (e.g. admin Users) opt in to keeping more than one
     * selected row. Defaults to single selection, which every pre-existing
     * consumer relies on.
     */
    enableMultiRowSelection?: boolean;
    /**
     * Per-row selectability. A row that returns false can't be toggled, and
     * `toggleAllPageRowsSelected` skips it, so "Select all" respects the rule.
     */
    enableRowSelection?: boolean | ((row: Row<TData>) => boolean);
}

export function DataTable<TData, TValue>({
    data,
    columns,
    entityLabel,
    emptyState,
    errorState,
    filteredState,
    clearFiltersLabel = "Clear filters",
    isRecovering,
    recoveringState,
    rowCount,
    isLoading,
    isError,
    hasActiveFilters,
    onClearFilters,
    onRetry,
    pagination,
    onPaginationChange,
    sorting,
    onSortingChange,
    rowSelection,
    onRowSelectionChange,
    getRowId,
    enableMultiRowSelection = false,
    enableRowSelection = true,
}: DataTableProps<TData, TValue>) {
    const table = useReactTable({
        data,
        columns,
        rowCount,
        state: {
            pagination,
            ...(sorting ? { sorting } : {}),
            ...(rowSelection ? { rowSelection } : {}),
        },
        onPaginationChange,
        onSortingChange,
        onRowSelectionChange,
        getRowId,
        getCoreRowModel: getCoreRowModel(),
        manualPagination: true,
        manualSorting: true,
        enableRowSelection,
        enableMultiRowSelection,
    });

    const colCount = columns.length;
    const rows = table.getRowModel().rows;
    const isInconsistentPage =
        isRecovering !== undefined &&
        data.length === 0 &&
        (rowCount ?? 0) > 0 &&
        !isRecovering;

    const stateRow = (state: DataTableState, action?: React.ReactNode) => (
        <TableRow>
            <TableCell colSpan={colCount} className="h-48">
                <div className="flex flex-col items-center justify-center gap-3 text-center">
                    {state.icon}
                    <div>
                        <p className="text-sm font-medium">{state.title}</p>
                        <p className="text-muted-foreground mt-0.5 text-xs">
                            {state.body}
                        </p>
                    </div>
                    {action}
                </div>
            </TableCell>
        </TableRow>
    );

    return (
        <div>
            <div className="overflow-hidden rounded-md border">
                <Table>
                    <TableHeader className="bg-muted/50">
                        {table.getHeaderGroups().map((headerGroup) => (
                            <TableRow key={headerGroup.id}>
                                {headerGroup.headers.map((header) => {
                                    const sortable =
                                        Boolean(onSortingChange) &&
                                        header.column.getCanSort();
                                    const sorted = header.column.getIsSorted();

                                    return (
                                        <TableHead
                                            key={header.id}
                                            className={cn(
                                                "text-muted-foreground font-medium",
                                                header.column.columnDef.meta
                                                    ?.cellClassName
                                            )}
                                        >
                                            {header.isPlaceholder ? null : sortable ? (
                                                <Button
                                                    variant={"ghost"}
                                                    className={cn(
                                                        "hover:text-foreground -ml-2 flex items-center gap-1 rounded-sm px-2 py-1 transition-colors",
                                                        sorted &&
                                                            "text-foreground"
                                                    )}
                                                    onClick={header.column.getToggleSortingHandler()}
                                                >
                                                    {flexRender(
                                                        header.column.columnDef
                                                            .header,
                                                        header.getContext()
                                                    )}
                                                    {sorted === "asc" ? (
                                                        <ArrowUp className="ml-2 h-4 w-4" />
                                                    ) : sorted === "desc" ? (
                                                        <ArrowDown className="ml-2 h-4 w-4" />
                                                    ) : (
                                                        <ArrowUpDown className="ml-2 h-4 w-4" />
                                                    )}
                                                </Button>
                                            ) : (
                                                flexRender(
                                                    header.column.columnDef
                                                        .header,
                                                    header.getContext()
                                                )
                                            )}
                                        </TableHead>
                                    );
                                })}
                            </TableRow>
                        ))}
                    </TableHeader>
                    <TableBody>
                        {isLoading
                            ? Array.from({ length: 8 }).map((_, i) => (
                                  <TableRow key={`skeleton-${i}`}>
                                      {columns.map((column, j) => (
                                          <TableCell
                                              key={`skeleton-${i}-${j}`}
                                              className={
                                                  column.meta?.cellClassName
                                              }
                                          >
                                              <Skeleton
                                                  className={cn(
                                                      "h-4 rounded",
                                                      j === 0 ? "w-40" : "w-20"
                                                  )}
                                              />
                                          </TableCell>
                                      ))}
                                  </TableRow>
                              ))
                            : isError || isInconsistentPage
                              ? stateRow(
                                    errorState,
                                    onRetry && (
                                        <Button
                                            variant="outline"
                                            size="sm"
                                            onClick={() => onRetry()}
                                        >
                                            Retry
                                        </Button>
                                    )
                                )
                              : isRecovering && recoveringState
                                ? stateRow(recoveringState)
                                : rows.length
                                  ? rows.map((row) => (
                                        <TableRow key={row.id}>
                                            {row
                                                .getVisibleCells()
                                                .map((cell) => (
                                                    <TableCell
                                                        key={cell.id}
                                                        className={
                                                            cell.column
                                                                .columnDef.meta
                                                                ?.cellClassName
                                                        }
                                                    >
                                                        {flexRender(
                                                            cell.column
                                                                .columnDef.cell,
                                                            cell.getContext()
                                                        )}
                                                    </TableCell>
                                                ))}
                                        </TableRow>
                                    ))
                                  : hasActiveFilters && filteredState
                                    ? stateRow(
                                          filteredState,
                                          onClearFilters && (
                                              <Button
                                                  variant="outline"
                                                  size="sm"
                                                  onClick={onClearFilters}
                                              >
                                                  {clearFiltersLabel}
                                              </Button>
                                          )
                                      )
                                    : stateRow(emptyState)}
                    </TableBody>
                </Table>
            </div>
            <div className="py-4">
                <DataTablePagination table={table} entityLabel={entityLabel} />
            </div>
        </div>
    );
}
